/**
 * Pure logic of Bobo, the Happy mascot (port of the admin `Bobo/boboPose.js` + `useBoboInteractions.js`
 * bookkeeping). No React, no React-Native: it only decides WHICH parts are drawn and WHICH body motion plays,
 * so it is unit-testable without a renderer. `Bobo.js` turns the result into SVG + reanimated timelines.
 *
 * @module components/bobo/boboLogic
 */

/** One-shot reactions and how long each one plays (ms). Must match the timelines in `Bobo.js`. */
export const REACTION_MS = Object.freeze({
    hop: 800,
    spin: 1300,
    dizzy: 2600,
    love: 2800,
});

/** Taps inside `DIZZY_WINDOW_MS` that make Bobo dizzy. */
export const DIZZY_CLICKS = 5;
export const DIZZY_WINDOW_MS = 2500;

/** Two taps this close together are a double tap (Bobo twirls). */
export const DOUBLE_TAP_MS = 320;

/** Lifetime of a particle burst (hearts / sparkles / stars) in ms. */
export const BURST_MS = 1700;

/** Idle actions that are pure body motions (no extra parts), mapped to the motion that draws them. */
const ACTION_MOTION = Object.freeze({
    hop: 'hopTwice',
    dance: 'dance',
    lookAround: 'look',
});

/** Reactions that play a body motion. */
const REACTION_MOTION = Object.freeze({
    hop: 'hop',
    spin: 'spin',
    dizzy: 'dizzy',
    squish: 'squish',
    love: 'love',
});

/** Forced faces that are allowed through `expression`. */
const EXPRESSIONS = Object.freeze(['happy', 'love', 'dizzy', 'surprised', 'wink']);

/** Named situations the screens use instead of the low-level flags. */
const MOODS = Object.freeze({
    happy: { expression: 'happy' },
    worried: { hasError: true },
    thinking: { action: 'think' },
    celebrate: { celebrate: true },
    love: { expression: 'love' },
    surprised: { expression: 'surprised' },
    sleepy: { action: 'sit' },
    greet: { greet: true },
});

/** Moods in which Bobo may also run his idle cycle (anything else has a point to make and stands still). */
export const IDLE_MOODS = Object.freeze([null, undefined, 'happy']);

/**
 * Translate a named mood into `deriveBoboPose` input.
 *
 * @param {(string|null)} [mood] 'happy' | 'worried' | 'thinking' | 'celebrate' | 'love' | 'surprised' | 'sleepy' | 'greet'.
 * @returns {Object} The matching pose input (empty for an unknown / missing mood).
 */
export function moodToProps(mood) {
    return MOODS[mood] || {};
}

/**
 * Pure tap bookkeeping: what does this tap make Bobo do?
 *
 * @param {number[]} history Timestamps of the recent taps.
 * @param {number} now Timestamp of this tap.
 * @returns {{ history: number[], reaction: string }} The pruned history and the reaction ('hop' | 'spin' | 'dizzy').
 */
export function registerTap(history, now) {
    const recent = [...history.filter((time) => now - time <= DIZZY_WINDOW_MS), now];

    if (recent.length >= DIZZY_CLICKS) {
        return {
            history: [],
            reaction: 'dizzy',
        };
    }
    const previous = recent.length > 1 ? recent[recent.length - 2] : null;

    return {
        history: recent,
        reaction: previous !== null && now - previous <= DOUBLE_TAP_MS ? 'spin' : 'hop',
    };
}

/**
 * Derive the drawing flags and the body motion of Bobo.
 *
 * @param {Object} [state] Current situation.
 * @param {(string|null)} [state.action] Idle action: 'wave' | 'think' | 'turn' | 'sitDown' | 'sit' | 'standUp' |
 *   'lookAround' | 'hop' | 'dance'.
 * @param {(string|null)} [state.reaction] Touch reaction: 'hop' | 'spin' | 'dizzy' | 'squish' | 'love'.
 * @param {(string|null)} [state.expression] Forced face: 'happy' | 'love' | 'dizzy' | 'surprised' | 'wink'.
 * @param {boolean} [state.coverEyes] Cover the eyes.
 * @param {boolean} [state.hasError] Worried face.
 * @param {boolean} [state.celebrate] Jump for joy.
 * @param {boolean} [state.saving] Open mouth while saving.
 * @param {boolean} [state.greet] Wave and smile.
 * @param {boolean} [state.shake] One-shot head shake.
 * @returns {Object} Flags used by `Bobo.js`; `motion` is '' (rest) or the name of the body timeline.
 */
export function deriveBoboPose(state = {}) {
    const {
        action = null, reaction = null, expression = null, coverEyes = false, hasError = false, celebrate = false,
        saving = false, greet = false, shake = false,
    } = state;
    const thinking = action === 'think';
    const turned = action === 'turn';
    const sitting = action === 'sit';
    const sittingDown = action === 'sitDown';
    const standingUp = action === 'standUp';
    const dancing = action === 'dance';
    const settled = !sitting && !sittingDown && !standingUp && !turned;
    const activeReaction = settled ? reaction : null;
    const hopping = activeReaction === 'hop' || (settled && action === 'hop');
    const excited = celebrate || greet;
    const worried = hasError && !coverEyes && !celebrate;
    const armsUp = celebrate || standingUp || dancing || hopping;
    const dizzy = activeReaction === 'dizzy' || expression === 'dizzy';
    const loving = activeReaction === 'love' || expression === 'love';

    let motion = '';

    if (shake) {
        motion = 'shake';
    } else if (sitting || sittingDown) {
        motion = sittingDown ? 'sitDown' : 'sit';
    } else if (standingUp) {
        motion = 'standUp';
    } else if (turned) {
        motion = 'turn';
    } else if (activeReaction && REACTION_MOTION[activeReaction]) {
        motion = REACTION_MOTION[activeReaction];
    } else if (celebrate) {
        motion = 'jump';
    } else if (ACTION_MOTION[action]) {
        motion = ACTION_MOTION[action];
    }

    let mouth = 'smile';

    if (worried || thinking) {
        mouth = 'flat';
    } else if (expression === 'surprised') {
        mouth = 'o';
    } else if (dizzy) {
        mouth = 'flat';
    } else if (excited || saving || standingUp || dancing || hopping || activeReaction === 'spin') {
        mouth = 'open';
    } else if (loving || activeReaction === 'squish' || expression === 'happy' || expression === 'wink') {
        mouth = 'cat';
    }

    let eyes = 'normal';

    if (!(coverEyes || turned || sitting || sittingDown || thinking || worried)) {
        if (EXPRESSIONS.includes(expression)) {
            eyes = expression;
        } else if (dizzy) {
            eyes = 'dizzy';
        } else if (loving) {
            eyes = 'love';
        } else if (hopping || dancing || celebrate || activeReaction === 'squish') {
            eyes = 'happy';
        }
    }

    return {
        thinking,
        turned,
        sitting: sitting || sittingDown,
        armsCover: coverEyes && !turned,
        armsUp: armsUp && !coverEyes,
        armThink: thinking && !coverEyes,
        armsNormal: !coverEyes && !armsUp && !thinking,
        armRightWave: excited || action === 'wave',
        worried,
        mouth,
        eyes,
        stars: dizzy && !coverEyes,
        motion,
    };
}

export default deriveBoboPose;
